import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Bot, CheckCircle2, Clock3, Download, FileSearch, RefreshCw, Search, ShieldCheck, Sparkles, Tags } from 'lucide-react';
import api from '../api';
import ErrorAlert from '../components/ErrorAlert';
import {
  CandidateSelect,
  CountList,
  MetricCard,
  Panel,
  QualityTable,
  RunsTable,
  StatusPill,
  TabButton,
  countStatuses,
  downloadCardEnrichmentCandidateArtifacts,
  downloadJobArtifact,
  percent,
  qualityRowsFor,
  sourceTotal,
  statusClass,
  statusCountsForRuns,
} from './agentWorkbench/AgentWorkbenchParts';

const LIVE_DIAGNOSTIC_SECTIONS = ['flags', 'flow', 'orchestration', 'runs', 'quality', 'enrichment'];

function preferredCandidateKey(candidates, readyKey, selectedKey = '') {
  const rows = candidates || [];
  if (selectedKey && rows.some(candidate => candidate.key === selectedKey)) return selectedKey;
  return rows.find(candidate => candidate[readyKey])?.key || rows[0]?.key || '';
}

function selectedCandidate(candidates, key) {
  return (candidates || []).find(candidate => candidate.key === key) || null;
}

function FlowPanel({ flow }) {
  const cap = Number(flow.budget_cap || 0);
  const pending = Number(flow.total_pending_items || 0);
  const remaining = Number(flow.budget_remaining || 0);
  return (
    <Panel title="Flow">
      <div className="grid gap-3 sm:grid-cols-3">
        <MetricCard label="Change requests" value={flow.pending_change_requests || 0} />
        <MetricCard label="Fields" value={flow.pending_fields || 0} />
        <MetricCard label="Signals" value={flow.pending_signals || 0} />
      </div>
      {cap > 0 && (
        <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-3">
          <div className="flex flex-wrap items-center justify-between gap-2 text-xs font-medium uppercase tracking-wide text-gray-500">
            <span>Backlog Budget</span>
            <span>{pending} / {cap} pending · {remaining} remaining</span>
          </div>
          <div className="mt-2 h-2 overflow-hidden rounded-full bg-white">
            <div
              className={`h-full rounded-full ${pending >= cap ? 'bg-red-500' : pending >= cap * 0.8 ? 'bg-amber-500' : 'bg-emerald-500'}`}
              style={{ width: `${Math.min(Math.max(Number(flow.budget_used_ratio || 0) * 100, 0), 100)}%` }}
            />
          </div>
          <p className="mt-2 text-xs text-gray-500">Advisory until scheduled worker enqueueing is enabled.</p>
        </div>
      )}
      <div className="mt-4 space-y-3">
        <div>
          <div className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">By Source</div>
          <CountList rows={flow.by_source} />
        </div>
        <div>
          <div className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">By Tier</div>
          <CountList rows={flow.by_tier} />
        </div>
      </div>
    </Panel>
  );
}

function OrchestrationPanel({ orchestration }) {
  const workers = orchestration?.workers || [];
  const active = orchestration?.active_work || {};
  const budget = orchestration?.pending_budget || {};
  return (
    <Panel title="Orchestration" accessory={<Bot className="h-4 w-4 text-gray-500" />}>
      <div className="grid gap-3 md:grid-cols-4">
        <MetricCard icon={Clock3} label="Queued Work" value={active.queued || 0} tone={active.queued ? 'amber' : 'slate'} />
        <MetricCard icon={Sparkles} label="Running Work" value={active.running || 0} tone={active.running ? 'blue' : 'slate'} />
        <MetricCard icon={AlertTriangle} label="Failed Work" value={active.failed || 0} tone={active.failed ? 'red' : 'green'} />
        <MetricCard icon={ShieldCheck} label="Pending Budget" value={`${budget.pending_items || 0}/${budget.cap || 250}`} tone={budget.over_cap ? 'red' : 'green'} />
      </div>
      <div className="mt-4 overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-4 py-2 text-left font-medium">Worker</th>
              <th className="px-4 py-2 text-left font-medium">Queue</th>
              <th className="px-4 py-2 text-left font-medium">Mode</th>
              <th className="px-4 py-2 text-left font-medium">Capability</th>
              <th className="px-4 py-2 text-right font-medium">Queued</th>
              <th className="px-4 py-2 text-right font-medium">Running</th>
              <th className="px-4 py-2 text-right font-medium">Failed</th>
              <th className="px-4 py-2 text-left font-medium">Next Gate</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {workers.map(worker => (
              <tr key={worker.id} className="align-top">
                <td className="px-4 py-3">
                  <div className="font-medium text-gray-900">{worker.label}</div>
                  <div className="text-xs text-gray-500">{(worker.enabled_commands || []).length ? worker.enabled_commands.join(', ') : (worker.command_ids || []).join(', ') || 'manual job'}</div>
                </td>
                <td className="px-4 py-3 font-mono text-xs text-gray-700">{worker.queue_table}</td>
                <td className="px-4 py-3 text-gray-700">{worker.volume_mode}</td>
                <td className="px-4 py-3"><StatusPill>{worker.capability_status}</StatusPill></td>
                <td className="px-4 py-3 text-right">{worker.queued_count || 0}</td>
                <td className="px-4 py-3 text-right">{worker.running_count || 0}</td>
                <td className={`px-4 py-3 text-right ${worker.failed_count ? 'font-semibold text-red-700' : 'text-gray-700'}`}>{worker.failed_count || 0}</td>
                <td className="max-w-md px-4 py-3 text-xs text-gray-600">{worker.next_gate}</td>
              </tr>
            ))}
            {!workers.length && (
              <tr><td colSpan="8" className="px-4 py-8 text-center text-sm text-gray-500">No worker orchestration data yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

function FlagsPanel({ workerCommands, registry, warningRegistryJob, warningRegistryRunning, onWarningRegistryCheck }) {
  const registryJobSummary = warningRegistryJob?.result?.summary || {};
  return (
    <Panel title="Flags & Registry Health" accessory={<Bot className="h-4 w-4 text-gray-500" />}>
      <div className="grid gap-4 lg:grid-cols-2">
        <div>
          <div className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">Worker Commands</div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(workerCommands).map(([command, enabled]) => (
              <span key={command} className={`rounded-full border px-2 py-1 text-xs font-medium ${enabled ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-gray-200 bg-gray-50 text-gray-600'}`}>
                {command.replace(/_/g, ' ')} · {enabled ? 'on' : 'off'}
              </span>
            ))}
          </div>
        </div>
        <div>
          <div className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">Warning Registry</div>
          <div className="grid gap-2 sm:grid-cols-5">
            <MetricCard label="Registered" value={registry.registered_codes || 0} />
            <MetricCard label="Blocking" value={registry.blocking_codes || 0} />
            <MetricCard label="Demoting" value={registry.demoting_codes || 0} />
            <MetricCard label="Unregistered" value={registry.unregistered_count || 0} tone={registry.unregistered_count ? 'red' : 'green'} />
            <MetricCard label="Declared Missing" value={registry.unregistered_declared_worker_warning_codes || 0} tone={registry.unregistered_declared_worker_warning_codes ? 'red' : 'green'} />
          </div>
          {(registry.unregistered_codes || []).length > 0 && (
            <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
              Stored without registry: {(registry.unregistered_codes || []).join(', ')}
            </div>
          )}
          {(registry.unregistered_declared_codes || []).length > 0 && (
            <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
              Declared by workers without registry: {(registry.unregistered_declared_codes || []).join(', ')}
            </div>
          )}
          <div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
            <div className="text-sm text-gray-600">
              Run the DB registry and worker warning-code contract check. Read-only; downloads CSV and Markdown reports.
            </div>
            <button
              type="button"
              onClick={onWarningRegistryCheck}
              disabled={warningRegistryRunning}
              className="inline-flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-medium text-blue-700 hover:bg-blue-100 disabled:opacity-50"
            >
              <ShieldCheck className="h-4 w-4" />
              {warningRegistryRunning ? 'Checking...' : 'Run registry check'}
            </button>
          </div>
          {warningRegistryJob && (
            <div className="mt-3 rounded-lg border border-gray-200 bg-white p-3 text-sm text-gray-700">
              Registry check · <StatusPill>{warningRegistryJob.status}</StatusPill>
              {' '}
              {warningRegistryJob.progress?.message || ''}
              {registryJobSummary.unregistered_warning_codes !== undefined ? ` · stored missing ${registryJobSummary.unregistered_warning_codes}` : ''}
              {registryJobSummary.unregistered_declared_worker_warning_codes !== undefined ? ` · declared missing ${registryJobSummary.unregistered_declared_worker_warning_codes}` : ''}
            </div>
          )}
        </div>
      </div>
    </Panel>
  );
}

function WorkerDetail({ title, metrics, runs, qualityRows, flowRows, retrying, onRetry, emptyQualityText, action, notice, extra }) {
  return (
    <div className="space-y-4">
      {(action || notice) && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3">
          <div className="text-sm text-gray-600">{notice}</div>
          {action}
        </div>
      )}
      <div className="grid gap-3 md:grid-cols-4">
        {metrics.map(metric => (
          <MetricCard key={metric.label} {...metric} />
        ))}
      </div>
      {extra}
      <div className="grid gap-4 xl:grid-cols-2">
        <Panel title={`${title} Quality`}>
          <QualityTable rows={qualityRows} emptyText={emptyQualityText} />
        </Panel>
        <Panel title="Pending By Source">
          <CountList rows={flowRows} />
        </Panel>
      </div>
      <Panel title={`${title} Runs`} accessory={<CountList rows={statusCountsForRuns(runs)} />}>
        <RunsTable items={runs} retrying={retrying} onRetry={onRetry} />
      </Panel>
    </div>
  );
}

function EnrichmentProvenancePanel({ summary }) {
  const totalFields = Number(summary.enrichment_field_proposals || 0);
  const provenanceFields = Number(summary.field_proposals_with_provenance || 0);
  const coverage = totalFields ? provenanceFields / totalFields : 0;
  return (
    <Panel title="Extraction & Provenance">
      <div className="grid gap-3 md:grid-cols-4">
        <MetricCard icon={Sparkles} label="Extraction runs" value={summary.runs_with_attribute_extraction || 0} tone="blue" />
        <MetricCard icon={AlertTriangle} label="Thin stops" value={summary.thin_runs || 0} tone={summary.thin_runs ? 'amber' : 'slate'} />
        <MetricCard icon={ShieldCheck} label="Provenance coverage" value={totalFields ? percent(coverage) : '—'} tone={coverage >= 0.9 || totalFields === 0 ? 'green' : 'amber'} />
        <MetricCard icon={Clock3} label="Attributes read" value={summary.extracted_attributes || 0} tone="slate" />
      </div>
      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <div>
          <div className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">Attribute Basis</div>
          <CountList rows={summary.attribute_basis_counts} />
        </div>
        <div>
          <div className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">Proposal Basis</div>
          <CountList rows={summary.proposal_basis_counts} />
          {summary.field_proposals_missing_provenance > 0 && (
            <p className="mt-2 text-xs text-amber-700">
              {summary.field_proposals_missing_provenance} enrichment field proposal(s) have no provenance metadata.
            </p>
          )}
        </div>
      </div>
    </Panel>
  );
}

function EnrichmentPipelinePanel({ pipeline }) {
  const stageCounts = pipeline?.stage_counts || {};
  const latestRuns = pipeline?.latest_runs || [];
  const stages = [
    ['source', 'Source'],
    ['evidence', 'Evidence'],
    ['signals', 'Signals'],
    ['prose', 'Prose'],
    ['validation', 'Validation'],
    ['proposals', 'Proposals'],
  ];
  return (
    <Panel title="Preparation Pipeline">
      <div className="grid gap-3 md:grid-cols-6">
        {stages.map(([stage, label]) => {
          const counts = stageCounts[stage] || {};
          const failed = Number(counts.failed || 0);
          const blocked = Number(counts.blocked || 0);
          const running = Number(counts.running || 0);
          const review = Number(counts.needs_review || 0);
          const completed = Number(counts.completed || 0);
          const tone = failed || blocked ? 'red' : running ? 'blue' : review ? 'amber' : completed ? 'green' : 'slate';
          return (
            <MetricCard
              key={stage}
              label={label}
              value={failed || blocked || running || review || completed || 0}
              tone={tone}
            />
          );
        })}
      </div>
      <div className="mt-4 overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-4 py-2 text-left font-medium">Run</th>
              <th className="px-4 py-2 text-left font-medium">Card</th>
              <th className="px-4 py-2 text-left font-medium">Status</th>
              <th className="px-4 py-2 text-left font-medium">Current Stage</th>
              <th className="px-4 py-2 text-left font-medium">Stage Trail</th>
              <th className="px-4 py-2 text-left font-medium">Message</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {latestRuns.map(run => {
              const activeStage = (run.stages || []).find(stage => stage.stage === run.blocked_stage) || {};
              return (
                <tr key={run.run_id} className="align-top">
                  <td className="px-4 py-3 font-mono text-xs text-gray-700">{run.run_id}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{run.place_master_id || '—'}</div>
                    <div className="text-xs text-gray-500">{run.card_key || 'primary'}</div>
                  </td>
                  <td className="px-4 py-3"><StatusPill>{run.status}</StatusPill></td>
                  <td className="px-4 py-3"><StatusPill>{run.blocked_stage}</StatusPill></td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {(run.stages || []).map(stage => (
                        <span
                          key={`${run.run_id}-${stage.stage}`}
                          className={`rounded-full border px-2 py-0.5 text-xs font-medium ${statusClass(stage.status)}`}
                          title={stage.message || ''}
                        >
                          {stage.label}: {String(stage.status || '').replace(/_/g, ' ')}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="max-w-sm px-4 py-3 text-xs text-gray-600">{activeStage.message || run.error || '—'}</td>
                </tr>
              );
            })}
            {!latestRuns.length && (
              <tr><td colSpan="6" className="px-4 py-8 text-center text-sm text-gray-500">No enrichment preparation runs yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

function ModelCandidateReadinessPanel({
  candidates,
  title = 'Model Candidate Readiness',
  firstLabel = 'Dry Run',
  secondLabel = 'Pilot',
  firstReadyKey = 'ready_for_dry_run',
  secondReadyKey = 'ready_for_pilot',
}) {
  const rows = candidates || [];
  if (!rows.length) return null;
  return (
    <Panel title={title}>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-4 py-2 text-left font-medium">Candidate</th>
              <th className="px-4 py-2 text-left font-medium">{firstLabel}</th>
              <th className="px-4 py-2 text-left font-medium">{secondLabel}</th>
              <th className="px-4 py-2 text-left font-medium">Missing Gates</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map(candidate => (
              <tr key={candidate.key} className="align-top">
                <td className="px-4 py-3">
                  <div className="font-medium text-gray-900">{candidate.key?.replace(/_/g, ' ')}</div>
                  <div className="text-xs text-gray-500">{candidate.provider} · {candidate.model}</div>
                </td>
                <td className="px-4 py-3">
                  <StatusPill>{candidate[firstReadyKey] ? 'ready' : 'blocked'}</StatusPill>
                </td>
                <td className="px-4 py-3">
                  <StatusPill>{candidate[secondReadyKey] ? 'ready' : 'blocked'}</StatusPill>
                </td>
                <td className="px-4 py-3 font-mono text-xs text-gray-600">
                  {(candidate.missing_gates || []).length ? candidate.missing_gates.join(', ') : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

function ReportsPanel({ attributeProseAuditJob, attributeProseAuditRunning, onRunAttributeProseAudit }) {
  const summary = attributeProseAuditJob?.result?.summary || {};
  return (
    <Panel title="Reports" accessory={<FileSearch className="h-4 w-4 text-gray-500" />}>
      <div className="grid gap-3 md:grid-cols-4">
        <MetricCard icon={Sparkles} label="Runs Scanned" value={summary.completed_runs_scanned || 0} tone="blue" />
        <MetricCard icon={CheckCircle2} label="Attributes" value={summary.attributes || 0} tone="slate" />
        <MetricCard icon={FileSearch} label="Cited By Prose" value={summary.attributes_cited_by_pitch_or_knows || 0} tone="green" />
        <MetricCard icon={AlertTriangle} label="Uncited" value={summary.attributes_not_cited_by_pitch_or_knows || 0} tone={summary.attributes_not_cited_by_pitch_or_knows ? 'amber' : 'slate'} />
      </div>
      <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-sm font-medium text-gray-900">Pitch/Knows attribute usage</div>
            <p className="mt-1 text-sm text-gray-600">
              Read-only audit over recent completed enrichment runs. Downloads detail, summary, and JSON artifacts.
            </p>
          </div>
          <button
            type="button"
            onClick={onRunAttributeProseAudit}
            disabled={attributeProseAuditRunning}
            className="inline-flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-medium text-blue-700 hover:bg-blue-100 disabled:opacity-50"
          >
            <FileSearch className="h-4 w-4" />
            {attributeProseAuditRunning ? 'Running...' : 'Run report'}
          </button>
        </div>
        {attributeProseAuditJob && (
          <div className="mt-3 rounded-lg border border-gray-200 bg-white p-3 text-sm text-gray-700">
            Attribute audit · <StatusPill>{attributeProseAuditJob.status}</StatusPill>
            {' '}
            {attributeProseAuditJob.progress?.message || ''}
            {summary.aggregate_rows !== undefined ? ` · ${summary.aggregate_rows} grouped rows` : ''}
          </div>
        )}
      </div>
    </Panel>
  );
}

function SourceIntelligencePanel({ capability, onToggle, toggling }) {
  const info = capability || {};
  const enabled = Boolean(info.enabled);
  const ready = Boolean(info.ready_for_pilot);
  const missingGates = info.missing_gates || [];
  const configSource = info.config_source === 'agent_config' ? 'AgentConfig' : 'environment fallback';
  return (
    <Panel
      title="Source Intelligence"
      accessory={<StatusPill>{ready ? 'ready' : enabled ? 'gated' : 'blocked'}</StatusPill>}
    >
      <div className="grid gap-3 md:grid-cols-3">
        <MetricCard label="Scraper model" value={enabled ? 'enabled' : 'off'} tone={enabled ? 'green' : 'slate'} />
        <MetricCard label="Pilot gate" value={ready ? 'ready' : 'blocked'} tone={ready ? 'green' : 'amber'} />
        <MetricCard label="Candidate" value={info.candidate_key || '—'} tone="blue" />
      </div>
      <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="font-medium text-gray-900">Worker runtime flag</div>
            <div className="mt-1 font-mono text-xs">source_intelligence={enabled ? 'on' : 'off'} · {configSource}</div>
          </div>
          <button
            type="button"
            onClick={() => onToggle(!enabled)}
            disabled={toggling}
            className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium disabled:opacity-50 ${
              enabled
                ? 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                : 'border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100'
            }`}
          >
            {toggling ? 'Saving...' : enabled ? 'Turn off' : 'Turn on'}
          </button>
        </div>
        <p className="mt-2">
          This admin toggle stores the worker flag in AgentConfig. When ready, new successful scrapes attach a structured source dossier for enrichment.
        </p>
      </div>
      {missingGates.length > 0 && (
        <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3">
          <div className="mb-2 text-xs font-medium uppercase tracking-wide text-amber-800">Missing Gates</div>
          <div className="flex flex-wrap gap-2">
            {missingGates.map(gate => (
              <span key={gate} className="rounded-full border border-amber-200 bg-white px-2 py-1 font-mono text-xs text-amber-900">
                {gate}
              </span>
            ))}
          </div>
        </div>
      )}
    </Panel>
  );
}

function CandidateEvalGatePanel({ job }) {
  const summary = job?.result?.summary || null;
  const gate = summary?.acceptance_gate || null;
  if (!gate) return null;

  const metrics = gate.metrics || {};
  const thresholds = gate.thresholds || {};
  const failures = gate.failures || [];
  const taskCount = Number(metrics.tasks ?? summary.tasks ?? 0);
  const minTasks = Number(thresholds.min_tasks ?? 5);
  const validRate = Number(metrics.valid_rate ?? summary.valid_rate ?? 0);
  const minValidRate = Number(thresholds.min_valid_rate ?? 1);
  const flags = Number(metrics.flags ?? summary.flags ?? 0);
  const maxFlags = Number(thresholds.max_flags ?? 0);
  const repairRate = Number(metrics.contract_repair_rate ?? 0);
  const maxRepairRate = Number(thresholds.max_contract_repair_rate ?? 0.2);

  return (
    <Panel title="Candidate Eval Gate" accessory={<StatusPill>{gate.passed ? 'ready' : 'blocked'}</StatusPill>}>
      <div className="grid gap-3 md:grid-cols-4">
        <MetricCard label="Tasks" value={`${taskCount}/${minTasks}`} tone={taskCount >= minTasks ? 'green' : 'amber'} />
        <MetricCard label="Valid Rate" value={percent(validRate)} tone={validRate >= minValidRate ? 'green' : 'red'} />
        <MetricCard label="Flags" value={flags} tone={flags > maxFlags ? 'red' : 'green'} />
        <MetricCard label="Repair Rate" value={percent(repairRate)} tone={repairRate > maxRepairRate ? 'red' : 'green'} />
      </div>
      {failures.length ? (
        <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3">
          <div className="mb-2 text-xs font-medium uppercase tracking-wide text-amber-800">Blocking Checks</div>
          <ul className="space-y-1 text-sm text-amber-900">
            {failures.map(failure => (
              <li key={failure.code || failure.message}>
                <span className="font-medium">{String(failure.code || '').replace(/_/g, ' ')}</span>
                {failure.message ? ` · ${failure.message}` : ''}
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm font-medium text-emerald-800">
          Candidate eval cleared the pilot readiness gate.
        </div>
      )}
    </Panel>
  );
}

export default function AgentWorkbenchView() {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [retrying, setRetrying] = useState('');
  const [evalPackJob, setEvalPackJob] = useState(null);
  const [evalPackRunning, setEvalPackRunning] = useState(false);
  const [evalSourceSeedRunning, setEvalSourceSeedRunning] = useState(false);
  const [evalSourceSeedResult, setEvalSourceSeedResult] = useState(null);
  const [candidateEvalJob, setCandidateEvalJob] = useState(null);
  const [candidateEvalRunning, setCandidateEvalRunning] = useState(false);
  const [enrichmentCandidateKey, setEnrichmentCandidateKey] = useState('');
  const [semanticEvalPackJob, setSemanticEvalPackJob] = useState(null);
  const [semanticEvalPackRunning, setSemanticEvalPackRunning] = useState(false);
  const [semanticCandidateEvalJob, setSemanticCandidateEvalJob] = useState(null);
  const [semanticCandidateEvalRunning, setSemanticCandidateEvalRunning] = useState(false);
  const [semanticCandidateKey, setSemanticCandidateKey] = useState('');
  const [warningRegistryJob, setWarningRegistryJob] = useState(null);
  const [warningRegistryRunning, setWarningRegistryRunning] = useState(false);
  const [attributeProseAuditJob, setAttributeProseAuditJob] = useState(null);
  const [attributeProseAuditRunning, setAttributeProseAuditRunning] = useState(false);
  const [sourceIntelligenceToggling, setSourceIntelligenceToggling] = useState(false);
  const [activePane, setActivePane] = useState('overview');
  const [error, setError] = useState('');

  const load = useCallback(async ({ quiet = false } = {}) => {
    if (quiet) setRefreshing(true);
    else setLoading(true);
    try {
      setOverview(await api.getAgentWorkbenchOverview({ safe: 'true' }));
      setError('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadLiveDiagnostics = async () => {
    setRefreshing(true);
    setError('');
    try {
      const nextOverview = {
        ...(overview || {}),
        diagnostics: {
          ...(overview?.diagnostics || {}),
          section_errors: [],
          section_timings_ms: {},
          partial: false,
        },
      };
      const sectionErrors = [];
      const timings = {};
      for (const section of LIVE_DIAGNOSTIC_SECTIONS) {
        try {
          const payload = await api.getAgentWorkbenchSection(section);
          if (payload[section]) {
            nextOverview[section] = payload[section];
          }
          const diagnostics = payload.diagnostics || {};
          (diagnostics.section_errors || []).forEach(item => sectionErrors.push(item));
          Object.assign(timings, diagnostics.section_timings_ms || {});
        } catch (err) {
          sectionErrors.push({ section, error: err.message });
        }
      }
      nextOverview.diagnostics = {
        section_errors: sectionErrors,
        section_timings_ms: timings,
        partial: sectionErrors.length > 0,
      };
      setOverview(nextOverview);
    } finally {
      setRefreshing(false);
    }
  };

  const retryRun = async run => {
    const key = `${run.run_table}:${run.id}`;
    setRetrying(key);
    try {
      await api.retryAgentWorkbenchRun(run.run_table, run.id, 'Agent Workbench retry');
      await load({ quiet: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setRetrying('');
    }
  };

  const toggleSourceIntelligence = async enabled => {
    setSourceIntelligenceToggling(true);
    setError('');
    try {
      await api.updateSourceIntelligenceConfig({
        enabled,
        reason: enabled ? 'Enable source intelligence from Agent Workbench' : 'Disable source intelligence from Agent Workbench',
      });
      await loadLiveDiagnostics();
    } catch (err) {
      setError(err.message);
    } finally {
      setSourceIntelligenceToggling(false);
    }
  };

  const startEvalPack = async () => {
    setEvalPackRunning(true);
    setCandidateEvalJob(null);
    setError('');
    try {
      let finalJob = await api.startCardEnrichmentEvalPack({ limit: 20 });
      setEvalPackJob(finalJob);
      await api.streamAdminJob(finalJob.id, next => {
        finalJob = next;
        setEvalPackJob(next);
      });
      if (finalJob.status === 'succeeded') {
        const artifact = finalJob.result?.artifacts?.tasks_jsonl;
        if (!downloadJobArtifact(artifact, 'card_enrichment_eval_tasks.jsonl')) {
          throw new Error('Eval pack completed without a downloadable JSONL artifact');
        }
        downloadJobArtifact(
          finalJob.result?.artifacts?.source_selection_manifest_json,
          'card_enrichment_eval_source_manifest.json',
        );
        await load({ quiet: true });
      } else if (finalJob.status === 'failed') {
        throw new Error(finalJob.error || 'Eval pack generation failed');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setEvalPackRunning(false);
    }
  };

  const seedEvalSources = async () => {
    setEvalSourceSeedRunning(true);
    setError('');
    try {
      const result = await api.seedCardEnrichmentEvalSources({ target_tasks: 5, max_queue: 10 });
      setEvalSourceSeedResult(result);
      await load({ quiet: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setEvalSourceSeedRunning(false);
    }
  };

  const startCandidateEval = async () => {
    setCandidateEvalRunning(true);
    setEvalPackJob(null);
    setError('');
    try {
      const candidates = overview?.flags?.enrichment_model_candidates || [];
      const candidateKey = preferredCandidateKey(candidates, 'ready_for_dry_run', enrichmentCandidateKey);
      if (!candidateKey) throw new Error('No enrichment model candidates are configured');
      let finalJob = await api.startCardEnrichmentCandidateEval({
        candidate_key: candidateKey,
        limit: 5,
      });
      setCandidateEvalJob(finalJob);
      await api.streamAdminJob(finalJob.id, next => {
        finalJob = next;
        setCandidateEvalJob(next);
      });
      if (finalJob.status === 'succeeded') {
        const downloaded = downloadCardEnrichmentCandidateArtifacts(finalJob.result?.artifacts || {});
        if (!downloaded) {
          throw new Error('Candidate eval completed without downloadable artifacts');
        }
        await load({ quiet: true });
      } else if (finalJob.status === 'failed') {
        throw new Error(finalJob.error || 'Candidate eval failed');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setCandidateEvalRunning(false);
    }
  };

  const startCandidateEvalWithManualKey = async () => {
    const apiKey = window.prompt('Paste OpenAI API key for this one eval run. It is sent once and not stored.');
    if (!apiKey || !apiKey.trim()) return;
    setCandidateEvalRunning(true);
    setEvalPackJob(null);
    setError('');
    setCandidateEvalJob({
      status: 'running',
      progress: { message: 'Running one-time manual key eval' },
      result: null,
    });
    try {
      const candidates = overview?.flags?.enrichment_model_candidates || [];
      const candidateKey = candidates.some(candidate => candidate.key === 'openai_gpt_5_4_mini')
        ? 'openai_gpt_5_4_mini'
        : preferredCandidateKey(candidates, 'ready_for_dry_run', enrichmentCandidateKey);
      if (!candidateKey) throw new Error('No enrichment model candidates are configured');
      const result = await api.runCardEnrichmentCandidateEvalWithKey({
        candidate_key: candidateKey,
        limit: 5,
        api_key: apiKey.trim(),
      });
      const finalJob = {
        status: 'succeeded',
        progress: { message: 'Complete' },
        result,
      };
      setCandidateEvalJob(finalJob);
      const downloaded = downloadCardEnrichmentCandidateArtifacts(result?.artifacts || {});
      if (!downloaded) {
        throw new Error('Candidate eval completed without downloadable artifacts');
      }
      await load({ quiet: true });
    } catch (err) {
      setCandidateEvalJob(prev => ({
        ...(prev || {}),
        status: 'failed',
        error: err.message,
        progress: { message: 'Failed' },
      }));
      setError(err.message);
    } finally {
      setCandidateEvalRunning(false);
    }
  };

  const startSemanticEvalPack = async () => {
    setSemanticEvalPackRunning(true);
    setSemanticCandidateEvalJob(null);
    setError('');
    try {
      let finalJob = await api.startTaxonomySemanticEvalPack({ limit: 100 });
      setSemanticEvalPackJob(finalJob);
      await api.streamAdminJob(finalJob.id, next => {
        finalJob = next;
        setSemanticEvalPackJob(next);
      });
      if (finalJob.status === 'succeeded') {
        const artifacts = finalJob.result?.artifacts || {};
        const downloaded = [
          downloadJobArtifact(artifacts.candidate_template_csv, 'taxonomy_semantic_candidate_template.csv'),
          downloadJobArtifact(artifacts.tasks_jsonl, 'taxonomy_semantic_tasks.jsonl'),
        ].filter(Boolean).length;
        if (!downloaded) {
          throw new Error('Semantic eval pack completed without downloadable artifacts');
        }
        await load({ quiet: true });
      } else if (finalJob.status === 'failed') {
        throw new Error(finalJob.error || 'Semantic eval pack generation failed');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSemanticEvalPackRunning(false);
    }
  };

  const startSemanticCandidateEval = async () => {
    setSemanticCandidateEvalRunning(true);
    setSemanticEvalPackJob(null);
    setError('');
    try {
      const candidates = overview?.flags?.taxonomy_semantic_model_candidates || [];
      const candidateKey = preferredCandidateKey(candidates, 'ready_for_eval', semanticCandidateKey);
      if (!candidateKey) throw new Error('No semantic model candidates are configured');
      let finalJob = await api.startTaxonomySemanticCandidateEval({
        candidate_key: candidateKey,
        limit: 20,
      });
      setSemanticCandidateEvalJob(finalJob);
      await api.streamAdminJob(finalJob.id, next => {
        finalJob = next;
        setSemanticCandidateEvalJob(next);
      });
      if (finalJob.status === 'succeeded') {
        const artifacts = finalJob.result?.artifacts || {};
        const downloaded = [
          downloadJobArtifact(artifacts.candidate_template_csv, 'taxonomy_semantic_candidate_template.csv'),
          downloadJobArtifact(artifacts.tasks_jsonl, 'taxonomy_semantic_tasks.jsonl'),
          downloadJobArtifact(artifacts.candidate_outputs_jsonl, 'taxonomy_semantic_candidate_outputs.jsonl'),
          downloadJobArtifact(artifacts.candidate_rows_csv, 'taxonomy_semantic_candidate_rows.csv'),
          downloadJobArtifact(artifacts.eval_report_json, 'taxonomy_semantic_eval_report.json'),
        ].filter(Boolean).length;
        if (!downloaded) {
          throw new Error('Semantic candidate eval completed without downloadable artifacts');
        }
        await load({ quiet: true });
      } else if (finalJob.status === 'failed') {
        throw new Error(finalJob.error || 'Semantic candidate eval failed');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSemanticCandidateEvalRunning(false);
    }
  };

  const startWarningRegistryCheck = async () => {
    setWarningRegistryRunning(true);
    setError('');
    try {
      let finalJob = await api.startWarningRegistryCheck();
      setWarningRegistryJob(finalJob);
      await api.streamAdminJob(finalJob.id, next => {
        finalJob = next;
        setWarningRegistryJob(next);
      });
      if (finalJob.status === 'succeeded') {
        const artifacts = finalJob.result?.artifacts || {};
        const downloaded = [
          downloadJobArtifact(artifacts.warning_registry_markdown, 'warning_code_registry_report.md'),
          downloadJobArtifact(artifacts.warning_registry_csv, 'warning_code_registry_report.csv'),
        ].filter(Boolean).length;
        if (!downloaded) {
          throw new Error('Registry check completed without downloadable artifacts');
        }
        await load({ quiet: true });
      } else if (finalJob.status === 'failed') {
        throw new Error(finalJob.error || 'Warning registry check failed');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setWarningRegistryRunning(false);
    }
  };

  const startAttributeProseAudit = async () => {
    setAttributeProseAuditRunning(true);
    setError('');
    try {
      let finalJob = await api.startEnrichmentAttributeProseAudit({ since: '2026-07-24', limit: 500 });
      setAttributeProseAuditJob(finalJob);
      await api.streamAdminJob(finalJob.id, next => {
        finalJob = next;
        setAttributeProseAuditJob(next);
      });
      if (finalJob.status === 'succeeded') {
        const artifacts = finalJob.result?.artifacts || {};
        const downloaded = [
          downloadJobArtifact(artifacts.attribute_prose_usage_detail_csv, 'attribute_prose_usage_detail.csv'),
          downloadJobArtifact(artifacts.attribute_prose_usage_summary_csv, 'attribute_prose_usage_summary.csv'),
          downloadJobArtifact(artifacts.attribute_prose_usage_summary_json, 'attribute_prose_usage_summary.json'),
        ].filter(Boolean).length;
        if (!downloaded) {
          throw new Error('Attribute usage audit completed without downloadable artifacts');
        }
        await load({ quiet: true });
      } else if (finalJob.status === 'failed') {
        throw new Error(finalJob.error || 'Attribute usage audit failed');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setAttributeProseAuditRunning(false);
    }
  };

  if (loading) return <div className="flex h-52 items-center justify-center text-sm text-gray-500">Loading...</div>;

  const runs = overview?.runs || {};
  const quality = overview?.quality || {};
  const flow = overview?.flow || {};
  const orchestration = overview?.orchestration || {};
  const enrichment = overview?.enrichment || {};
  const flags = overview?.flags || {};
  const diagnostics = overview?.diagnostics || {};
  const workerCommands = flags.capabilities?.worker_commands || {};
  const sourceIntelligence = flags.capabilities?.source_intelligence || {};
  const registry = flags.warning_registry || {};
  const modelCandidates = flags.enrichment_model_candidates || [];
  const semanticModelCandidates = flags.taxonomy_semantic_model_candidates || [];
  const effectiveEnrichmentCandidateKey = preferredCandidateKey(modelCandidates, 'ready_for_dry_run', enrichmentCandidateKey);
  const effectiveSemanticCandidateKey = preferredCandidateKey(semanticModelCandidates, 'ready_for_eval', semanticCandidateKey);
  const effectiveEnrichmentCandidate = selectedCandidate(modelCandidates, effectiveEnrichmentCandidateKey);
  const effectiveSemanticCandidate = selectedCandidate(semanticModelCandidates, effectiveSemanticCandidateKey);
  const runItems = runs.items || [];
  const qualityItems = quality.items || [];
  const sourceCounts = flow.by_source || {};
  const scraperRuns = runItems.filter(run => run.worker === 'scrape_worker');
  const librarianRuns = runItems.filter(run => run.worker === 'taxonomy_librarian');
  const enrichmentRuns = runItems.filter(run => run.run_table === 'enrichment_runs' || String(run.worker || '').includes('enrichment'));
  const scraperQuality = qualityRowsFor(qualityItems, ['scrape', 'scrape_agent']);
  const librarianQuality = qualityRowsFor(qualityItems, ['taxonomy_librarian']);
  const enrichmentQuality = qualityRowsFor(qualityItems, ['enrichment_agent', 'card_enrichment_worker']);
  const scraperPending = sourceTotal(sourceCounts, ['scrape', 'scrape_agent']);
  const librarianPending = sourceTotal(sourceCounts, ['taxonomy_librarian']);
  const enrichmentPending = sourceTotal(sourceCounts, ['enrichment_agent', 'card_enrichment_worker']);
  const diagnosticErrors = diagnostics.section_errors || [];
  const realDiagnosticErrors = diagnosticErrors.filter(item => item.section !== 'safe_shell');
  const safeShellOnly = diagnostics.partial && diagnosticErrors.length > 0 && realDiagnosticErrors.length === 0;
  const tabs = [
    { id: 'overview', label: 'Overview', icon: Bot },
    { id: 'runs', label: 'Runs', icon: Clock3, count: runs.total_recent || 0 },
    { id: 'scraper', label: 'Scraper', icon: Search, count: scraperPending },
    { id: 'librarian', label: 'Librarian', icon: Tags, count: librarianPending },
    { id: 'enrichment', label: 'Enrichment', icon: Sparkles, count: enrichmentPending },
    { id: 'reports', label: 'Reports', icon: FileSearch },
  ];

  return (
    <div className="space-y-4 text-gray-900">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Agent Workbench</h1>
          <p className="text-sm text-gray-500">Worker QA and flow control. Verdicts stay in Change Proposals.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => load({ quiet: true })}
            disabled={refreshing}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
          </button>
          <button
            type="button"
            onClick={loadLiveDiagnostics}
            disabled={refreshing}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-900 bg-gray-900 px-3 py-2 text-sm text-white hover:bg-gray-800 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} /> Load live diagnostics
          </button>
        </div>
      </div>

      {error && <ErrorAlert error={error} />}
      {safeShellOnly && (
        <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
          <p className="font-medium">Live diagnostics are not loaded yet.</p>
          <p className="mt-1">Use Load live diagnostics when you want current worker status.</p>
        </div>
      )}
      {realDiagnosticErrors.length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <p className="font-medium">Agent Workbench loaded with partial diagnostics.</p>
          <div className="mt-2 grid gap-1">
            {realDiagnosticErrors.map(item => (
              <p key={item.section}>
                <span className="font-medium">{item.section}:</span> {item.error || 'section failed'}
              </p>
            ))}
          </div>
        </div>
      )}

      <div className="border-b border-gray-200">
        <div className="flex flex-wrap gap-1">
          {tabs.map(tab => (
            <TabButton
              key={tab.id}
              tab={tab}
              active={activePane === tab.id}
              onClick={() => setActivePane(tab.id)}
            />
          ))}
        </div>
      </div>

      {activePane === 'overview' && (
        <div className="space-y-4">
          <div className="grid gap-3 md:grid-cols-4">
            <MetricCard icon={Clock3} label="Recent Runs" value={runs.total_recent || 0} tone="blue" />
            <MetricCard icon={CheckCircle2} label="Reviewed Verdicts" value={quality.reviewed_field_verdicts || 0} tone="green" />
            <MetricCard icon={AlertTriangle} label="Pending Items" value={flow.total_pending_items || 0} tone={flow.total_pending_items ? 'amber' : 'slate'} />
            <MetricCard icon={ShieldCheck} label="Unregistered Codes" value={registry.unregistered_count || 0} tone={registry.unregistered_count ? 'red' : 'green'} />
          </div>
          <OrchestrationPanel orchestration={orchestration} />
          <div className="grid gap-4 xl:grid-cols-2">
            <Panel title="Quality">
              <QualityTable rows={qualityItems} />
            </Panel>
            <FlowPanel flow={flow} />
          </div>
          <FlagsPanel
            workerCommands={workerCommands}
            registry={registry}
            warningRegistryJob={warningRegistryJob}
            warningRegistryRunning={warningRegistryRunning}
            onWarningRegistryCheck={startWarningRegistryCheck}
          />
        </div>
      )}

      {activePane === 'runs' && (
        <Panel title="Runs - Latest 25 Rows" accessory={<CountList rows={runs.status_counts} />}>
          <RunsTable items={runItems} retrying={retrying} onRetry={retryRun} />
        </Panel>
      )}

      {activePane === 'reports' && (
        <ReportsPanel
          attributeProseAuditJob={attributeProseAuditJob}
          attributeProseAuditRunning={attributeProseAuditRunning}
          onRunAttributeProseAudit={startAttributeProseAudit}
        />
      )}

      {activePane === 'scraper' && (
        <WorkerDetail
          title="Scraper"
          metrics={[
            { icon: Search, label: 'Recent Runs', value: scraperRuns.length, tone: 'blue' },
            { icon: AlertTriangle, label: 'Failed Runs', value: countStatuses(scraperRuns, ['failed', 'error']), tone: countStatuses(scraperRuns, ['failed', 'error']) ? 'red' : 'green' },
            { icon: CheckCircle2, label: 'Pending Items', value: scraperPending, tone: scraperPending ? 'amber' : 'slate' },
            { icon: ShieldCheck, label: 'Commands On', value: ['scrape_site', 'refresh_sources', 'refresh_event_data'].filter(command => workerCommands[command]).length, tone: 'green' },
          ]}
          runs={scraperRuns}
          qualityRows={scraperQuality}
          flowRows={{ scrape_agent: sourceCounts.scrape_agent || 0, scrape: sourceCounts.scrape || 0 }}
          retrying={retrying}
          onRetry={retryRun}
          emptyQualityText="No reviewed scrape proposal fields yet."
          extra={(
            <SourceIntelligencePanel
              capability={sourceIntelligence}
              onToggle={toggleSourceIntelligence}
              toggling={sourceIntelligenceToggling}
            />
          )}
        />
      )}

      {activePane === 'librarian' && (
        <WorkerDetail
          title="Librarian"
          metrics={[
            { icon: Tags, label: 'Recent Jobs', value: librarianRuns.length, tone: 'blue' },
            { icon: CheckCircle2, label: 'Pending Items', value: librarianPending, tone: librarianPending ? 'amber' : 'slate' },
            { icon: ShieldCheck, label: 'Reviewed Verdicts', value: librarianQuality.reduce((total, row) => total + Number(row.total || 0), 0), tone: 'green' },
            { icon: Clock3, label: 'Last Status', value: librarianRuns[0]?.status || '—', tone: librarianRuns[0]?.status === 'failed' ? 'red' : 'slate' },
          ]}
          runs={librarianRuns}
          qualityRows={librarianQuality}
          flowRows={{ taxonomy_librarian: sourceCounts.taxonomy_librarian || 0 }}
          retrying={retrying}
          onRetry={retryRun}
          emptyQualityText="No reviewed Librarian proposal fields yet."
          extra={(
            <ModelCandidateReadinessPanel
              candidates={semanticModelCandidates}
              title="Semantic Candidate Readiness"
              firstLabel="Eval"
              secondLabel="Volume"
              firstReadyKey="ready_for_eval"
              secondReadyKey="ready_for_volume"
            />
          )}
          notice={semanticEvalPackJob ? (
            <span>
              Semantic pack · <StatusPill>{semanticEvalPackJob.status}</StatusPill>
              {' '}
              {semanticEvalPackJob.progress?.message || ''}
              {semanticEvalPackJob.result?.summary?.semantic_tasks !== undefined ? ` · ${semanticEvalPackJob.result.summary.semantic_tasks} tasks` : ''}
            </span>
          ) : semanticCandidateEvalJob ? (
            <span>
              Semantic eval · <StatusPill>{semanticCandidateEvalJob.status}</StatusPill>
              {' '}
              {semanticCandidateEvalJob.progress?.message || ''}
              {semanticCandidateEvalJob.result?.summary?.action_accuracy !== undefined ? ` · action ${percent(semanticCandidateEvalJob.result.summary.action_accuracy)}` : ''}
            </span>
          ) : 'Generate a read-only Librarian semantic task pack or run a gated model against it.'}
          action={(
            <div className="flex flex-wrap gap-2">
              <CandidateSelect
                label="Model"
                candidates={semanticModelCandidates}
                value={effectiveSemanticCandidateKey}
                onChange={setSemanticCandidateKey}
                readyKey="ready_for_eval"
              />
              <button
                type="button"
                onClick={startSemanticEvalPack}
                disabled={semanticEvalPackRunning || semanticCandidateEvalRunning}
                className="inline-flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-medium text-blue-700 hover:bg-blue-100 disabled:opacity-50"
              >
                <Download className="h-4 w-4" />
                {semanticEvalPackRunning ? 'Generating...' : 'Generate semantic pack'}
              </button>
              <button
                type="button"
                onClick={startSemanticCandidateEval}
                disabled={semanticEvalPackRunning || semanticCandidateEvalRunning || !effectiveSemanticCandidate?.ready_for_eval}
                className="inline-flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
              >
                <Sparkles className="h-4 w-4" />
                {semanticCandidateEvalRunning ? 'Running...' : 'Run semantic eval'}
              </button>
            </div>
          )}
        />
      )}

      {activePane === 'enrichment' && (
        <WorkerDetail
          title="Enrichment"
          metrics={[
            { icon: Sparkles, label: 'Recent Runs', value: enrichmentRuns.length, tone: 'blue' },
            { icon: AlertTriangle, label: 'Failed Runs', value: countStatuses(enrichmentRuns, ['failed', 'error']), tone: countStatuses(enrichmentRuns, ['failed', 'error']) ? 'red' : 'green' },
            { icon: CheckCircle2, label: 'Pending Items', value: enrichmentPending, tone: enrichmentPending ? 'amber' : 'slate' },
            { icon: ShieldCheck, label: 'Capability', value: workerCommands.enrich_card ? 'on' : 'off', tone: workerCommands.enrich_card ? 'green' : 'slate' },
          ]}
          runs={enrichmentRuns}
          qualityRows={enrichmentQuality}
          flowRows={{
            enrichment_agent: sourceCounts.enrichment_agent || 0,
            card_enrichment_worker: sourceCounts.card_enrichment_worker || 0,
          }}
          retrying={retrying}
          onRetry={retryRun}
          emptyQualityText="No reviewed enrichment proposal fields yet."
          extra={(
            <>
              <EnrichmentPipelinePanel pipeline={enrichment.pipeline} />
              <ModelCandidateReadinessPanel candidates={modelCandidates} />
              <CandidateEvalGatePanel job={candidateEvalJob} />
              <EnrichmentProvenancePanel summary={enrichment} />
            </>
          )}
          notice={evalPackJob ? (
            <span>
              Eval pack · <StatusPill>{evalPackJob.status}</StatusPill>
              {' '}
              {evalPackJob.progress?.message || ''}
              {evalPackJob.result?.summary?.tasks !== undefined ? ` · ${evalPackJob.result.summary.tasks} tasks` : ''}
            </span>
          ) : evalSourceSeedResult ? (
            <span>
              Eval source seed · <StatusPill>{evalSourceSeedResult.queued_runs ? 'queued' : 'clear'}</StatusPill>
              {' '}
              {evalSourceSeedResult.current_tasks}/{evalSourceSeedResult.target_tasks} tasks ready
              {evalSourceSeedResult.queued_runs ? ` · ${evalSourceSeedResult.queued_runs} scrape(s) queued` : ''}
            </span>
          ) : candidateEvalJob ? (
            <span>
              Model eval · <StatusPill>{candidateEvalJob.status}</StatusPill>
              {' '}
              {candidateEvalJob.progress?.message || ''}
              {candidateEvalJob.result?.summary?.valid_outputs !== undefined ? ` · ${candidateEvalJob.result.summary.valid_outputs}/${candidateEvalJob.result.summary.tasks} valid` : ''}
              {candidateEvalJob.result?.summary?.acceptance_gate ? ` · gate ${candidateEvalJob.result.summary.acceptance_gate.passed ? 'passed' : 'blocked'}` : ''}
            </span>
          ) : 'Generate a read-only source-snapshot task pack for model evaluation.'}
          action={(
            <div className="flex flex-wrap gap-2">
              <CandidateSelect
                label="Model"
                candidates={modelCandidates}
                value={effectiveEnrichmentCandidateKey}
                onChange={setEnrichmentCandidateKey}
                readyKey="ready_for_dry_run"
              />
              <button
                type="button"
                onClick={startEvalPack}
                disabled={evalPackRunning || evalSourceSeedRunning || candidateEvalRunning}
                className="inline-flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-medium text-blue-700 hover:bg-blue-100 disabled:opacity-50"
              >
                <Download className="h-4 w-4" />
                {evalPackRunning ? 'Generating...' : 'Generate eval pack'}
              </button>
              <button
                type="button"
                onClick={seedEvalSources}
                disabled={evalPackRunning || evalSourceSeedRunning || candidateEvalRunning}
                className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                <Search className="h-4 w-4" />
                {evalSourceSeedRunning ? 'Queueing...' : 'Seed eval sources'}
              </button>
              <button
                type="button"
                onClick={startCandidateEval}
                disabled={evalPackRunning || evalSourceSeedRunning || candidateEvalRunning || !effectiveEnrichmentCandidate?.ready_for_dry_run}
                className="inline-flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
              >
                <Sparkles className="h-4 w-4" />
                {candidateEvalRunning ? 'Running...' : 'Run model eval'}
              </button>
              <button
                type="button"
                onClick={startCandidateEvalWithManualKey}
                disabled={evalPackRunning || evalSourceSeedRunning || candidateEvalRunning}
                className="inline-flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-800 hover:bg-amber-100 disabled:opacity-50"
              >
                <Sparkles className="h-4 w-4" />
                {candidateEvalRunning ? 'Running...' : 'Run with temp key'}
              </button>
            </div>
          )}
        />
      )}
    </div>
  );
}
